import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { components } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { Badge } from '../ui/badge'

export type RentalApplication = components['schemas']['RentalApplication']
export type ApplicationCard = components['schemas']['RentalApplicationCard']
export type ScreeningRow = components['schemas']['ScreeningRow']
export type Question = components['schemas']['RentalApplicationQuestion']
export type ScreeningCriterion = components['schemas']['ScreeningCriterion']
export type PolicyView = components['schemas']['ScreeningPolicyView']

/** Every stage, in pipeline order; the last four move in the viewing and offer flow. */
export const STAGES = ['enquiry', 'screening', 'screened', 'viewing', 'offer', 'offer_accepted', 'leased'] as const

const RESULT_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
    pass: 'success',
    review: 'warning',
    missing: 'secondary',
    fail: 'destructive',
}

/** A verdict or a criterion result: an explained label, never a score. */
export function ResultBadge({ result, testId }: { result?: string | null; testId?: string }) {
    const { t } = useTranslation('rentalApplications')
    if (!result) return null
    return (
        <Badge variant={RESULT_VARIANT[result] ?? 'secondary'} data-testid={testId} data-result={result}>
            {t(`result.${result}`, { defaultValue: result })}
        </Badge>
    )
}

export function OutcomeBadge({ outcome, testId }: { outcome?: string | null; testId?: string }) {
    const { t } = useTranslation('rentalApplications')
    if (!outcome) return null
    return (
        <Badge variant={outcome === 'refused' ? 'destructive' : 'outline'} data-testid={testId} data-outcome={outcome}>
            {t(`outcome.${outcome}`, { defaultValue: outcome })}
        </Badge>
    )
}

/** The label of pack content, in the reader's language. */
export function useLabel() {
    const { locale } = useLocale()
    return (label?: Record<string, string> | null, fallback = '') => (label ? getLocalizedName(label, locale) || fallback : fallback)
}

/** Refetch the application and screening queries after a write. */
export function useRefreshApplications() {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (q) => {
                const [method, path] = q.queryKey as [string, string]
                return (
                    method === 'get' &&
                    typeof path === 'string' &&
                    (path.startsWith('/api/v1/organizations/{orgCode}/rental-applications') ||
                        path.startsWith('/api/v1/organizations/{orgCode}/screening-'))
                )
            },
        })
}
